import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {DataStatus, useTranslationContext} from 'platform/components';

import {useCallback, useMemo, type MouseEvent, type ReactNode} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {EagGridSurface} from './components/GridMaximization/EagGridSurface';
import type {DataGridContext} from './context/DataGridContext';
import {useGridKeyboardNav} from './context/GridKeyboardNavContext';
import {GridKeyboardNavProvider} from './context/GridKeyboardNavProvider';
import {DataGridContextProvider} from './context/useDataGridContext';
import {EagGridTable} from './EagGridTable';
import {useAvailableSizePx} from './hooks/useAvailableSizePx';
import {useFilterHydration} from './hooks/useFilterHydration';
import {useGridLifecycle} from './hooks/useGridLifecycle';
import {useIsMobileView} from './hooks/useIsMobileView';
import {
  selectActivePreset,
  selectApiError,
  selectDataQuery,
  selectDefinition,
  selectVirtualPreset,
} from './store/createEagGridStore';
import {EagGridStoreProvider} from './store/EagGridStoreProvider';
import {selectWrapperStyle} from './store/selectors/useBehavior';
import {useIsGridMaximized} from './store/selectors/useIsGridMaximized';
import {useEagGridSelector} from './store/useEagGridSelector';
import {useEagGridStore} from './store/useEagGridStore';
import type {EagGridProps} from './types/EagGridProps';
import {getApiErrorMessage} from './utils/getApiErrorMessage';
import {getContainerStyle, getWrapperClassName} from './utils/getContainerStyle';
import {transformDefinition} from './utils/transformDefinition';

const PREVENT_CONTEXT_MENU = (e: MouseEvent) => {
  e.preventDefault();
};

type EagGridInteractiveBodyProps = {
  callbackRef: (node: HTMLDivElement | null) => void;
  wrapperClassName: string;
  containerStyle: React.CSSProperties | undefined;
  children: ReactNode;
} & TestIdProps;

function EagGridInteractiveBody(props: EagGridInteractiveBodyProps) {
  const nav = useGridKeyboardNav();
  return (
    <div
      ref={props.callbackRef}
      className={props.wrapperClassName}
      style={props.containerStyle}
      data-testid={suffixTestId('gridWrapper', props)}
      onContextMenu={PREVENT_CONTEXT_MENU}
      onKeyDown={nav?.handleKeyDown}
      onBlur={nav?.handleBlur}
    >
      {props.children}
    </div>
  );
}

const DESKTOP_BOTTOM_INSET_PX = 16;

/**
 * ## Referential stability contract (READ THIS before adding context values, callbacks, or refs)
 *
 * @mandatory All context values, callbacks, and ref compositions created in
 * EagGridInner MUST remain referentially stable across renders to prevent
 * cascading re-renders via zustand `useSyncExternalStore` subscribers.
 *
 * React 19's strict useSyncExternalStore consistency checks throw
 * "Maximum update depth exceeded" if a child component's useLayoutEffect — e.g.
 * EagGridTable's column-state setup `useLayoutEffect([columnConfig, store])`,
 * which calls `store.setColumnStates(...)` — runs because a parent re-render was
 * triggered by an unstable context value. Each useMemo/useCallback below is a
 * link in that chain; the dependency arrays document exactly when each value is
 * allowed to change. Individual sites cite this block rather than repeating it.
 */
function EagGridInner(props: EagGridProps<readonly string[]>) {
  const t = useTranslationContext();
  const store = useEagGridStore();
  const isMobile = useIsMobileView();
  const isRequested = useIsGridMaximized();
  const isMaximized = props.maximization?.enabled === true && isRequested;
  const autoHeight = isMaximized ? false : props.autoHeight;

  const {availableHeightPx, availableWidthPx, callbackRef} = useAvailableSizePx({
    bottomInset: isMobile ? 0 : DESKTOP_BOTTOM_INSET_PX,
    isBoundedByParent: isMaximized,
  });

  const dataQuery = useEagGridSelector(selectDataQuery);
  const virtualPreset = useEagGridSelector(selectVirtualPreset);
  const definition = useEagGridSelector(selectDefinition);
  const activePreset = useEagGridSelector(selectActivePreset);
  const apiError = useEagGridSelector(selectApiError);
  const wrapperStyle = useEagGridSelector(selectWrapperStyle);
  const predefinedColumns = props.predefinedColumns ?? [];

  // @mandatory: see EagGridInner "Referential stability contract" doc block above.
  const onApiError = useCallback(
    (error: FetchBaseQueryError) => {
      const errorMessage = getApiErrorMessage(
        error,
        t('general.notifications.defaultErrorMessage')
      );
      store.setApiError(errorMessage);
    },
    [store, t]
  );

  const isFilterHydrated = useFilterHydration({
    dataQuery,
    queryModifier: props.queryModifier,
    initialSmartSearch: props.initialSmartSearch,
    store,
  });

  const isDatagridLoading =
    !virtualPreset ||
    !definition ||
    !activePreset ||
    !dataQuery ||
    !isFilterHydrated ||
    isNotNil(apiError);

  const gridLifecycle = useGridLifecycle({
    gridCode: props.gridCode,
    presetInstanceId: props.presetInstanceId,
    behaviorOverrides: props._useAsLastResort_definitionBehaviorOverrides,
    disableAllActions: props.disableAllActions,
    hasSilentRefetching: props.hasSilentRefetching,
    actionsAlignment: props.actionsAlignment,
    queryModifier: props.queryModifier,
    onApiError,
    predefinedColumns,
    onExternalFilterChange: props.onExternalFilterChange,
    onFilterChanged: props.onFilterChanged,
  });

  // @mandatory: see EagGridInner "Referential stability contract" doc block above.
  const contextValue: DataGridContext = useMemo(
    () => ({
      externalFilterId: props.externalFilterId,
      externalFilterResourceId: props.externalFilterResourceId,
      externalFilterRecordId: props.externalFilterRecordId,
      onExternalFilterChange: props.onExternalFilterChange,
      onGridStateChange: props.onGridStateChange,
      queryModifier: props.queryModifier,
      filterComponent: props.filterComponent,
      bulkOperationActionCallback: props.bulkOperationActionCallback,
      pdfColumns: props.pdfColumns,
      onPdfExportCallback: props.onPdfExportCallback,
      onApiError,
      linkClickOverride: props._useAsLastResort_linkClickOverride,
      isRowHighlighted: props.isRowHighlighted,
    }),
    [
      props.externalFilterId,
      props.externalFilterResourceId,
      props.externalFilterRecordId,
      props.onExternalFilterChange,
      props.onGridStateChange,
      props.queryModifier,
      props.filterComponent,
      props.bulkOperationActionCallback,
      props.pdfColumns,
      props.onPdfExportCallback,
      onApiError,
      props._useAsLastResort_linkClickOverride,
      props.isRowHighlighted,
    ]
  );

  const dataTestId = props['data-testid'];
  // @mandatory: see EagGridInner "Referential stability contract" doc block above.
  const columnConfig = useMemo(
    () =>
      definition && activePreset && dataQuery
        ? transformDefinition({
            definition,
            activePreset,
            dataQuery,
            'data-testid': dataTestId,
          })
        : null,
    [definition, activePreset, dataQuery, dataTestId]
  );

  const containerStyle = getContainerStyle(
    autoHeight ?? false,
    availableHeightPx,
    availableWidthPx
  );

  const dataStatusMinHeight = autoHeight ? 20 : '100%';

  if (isNotNil(apiError)) {
    return <DataStatus isError errorMessage={apiError} minHeight={dataStatusMinHeight} />;
  }

  if (isDatagridLoading || !definition || !columnConfig) {
    return <DataStatus isLoading minHeight={dataStatusMinHeight} />;
  }

  return (
    <GridKeyboardNavProvider>
      <EagGridInteractiveBody
        callbackRef={callbackRef}
        wrapperClassName={getWrapperClassName(wrapperStyle, autoHeight ?? false, isMobile)}
        containerStyle={containerStyle}
        data-testid={props['data-testid']}
      >
        <DataGridContextProvider value={contextValue}>
          <EagGridTable
            gridCode={props.gridCode}
            presetInstanceId={props.presetInstanceId}
            emptyState={props.emptyState}
            actionCallback={props.actionCallback}
            onRowSelectionChange={props.onRowSelectionChange}
            onGridDataChange={props.onGridDataChange}
            externalFilterId={props.externalFilterId}
            autoHeight={autoHeight}
            maximization={props.maximization}
            columnsPanelScrollToEnabled={props.columnsPanelScrollToEnabled}
            drawerModeEnabled={props.drawerModeEnabled}
            filterByValueEnabled={props.filterByValueEnabled}
            queryModifier={props.queryModifier}
            definition={definition}
            columnConfig={columnConfig}
            data-testid={props['data-testid']}
            setVirtualPreset={store.setVirtualPreset}
            autoFocusSmartSearch={props.autoFocusSmartSearch}
            predefinedColumns={predefinedColumns}
            refreshData={gridLifecycle.refreshData}
            retryFetch={gridLifecycle.retryFetch}
            queryIdRef={gridLifecycle.queryIdRef}
            handleResetColumns={gridLifecycle.handleResetColumns}
          />
        </DataGridContextProvider>
      </EagGridInteractiveBody>
    </GridKeyboardNavProvider>
  );
}

export function EagGrid<T extends readonly string[] = never>(props: EagGridProps<T>) {
  // The cast to EagGridProps<readonly string[]> is safe — EagGridInner is private and the public
  // generic constraint is enforced at the EagGrid call site.
  return (
    <EagGridStoreProvider>
      <EagGridSurface
        maximization={props.maximization}
        sectionHeader={props.sectionHeader}
        maximizedSectionHeader={props.maximizedSectionHeader}
      >
        <EagGridInner {...(props as EagGridProps<readonly string[]>)} />
      </EagGridSurface>
    </EagGridStoreProvider>
  );
}
